import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { FormStep, HasNotary, RoutesPath } from '@features/succession/enums';
import { NotaryInformationForm } from '@features/succession/forms';
import { CustomerInformation, NotaryInformation } from '@features/succession/models';
import { SuccessionFacade } from '@features/succession/succession.facade';
import { ControlValidators } from '@shared/validators';

@Injectable({ providedIn: 'root' })
export class NotaryInformationFacade {
  protected destroyRef = inject(DestroyRef);

  private readonly successionFacade = inject(SuccessionFacade);

  private readonly formBuilder = inject(FormBuilder);

  public notaryInformation: NotaryInformation | undefined;

  public notaryInformationForm: FormGroup<NotaryInformationForm> =
    this.formBuilder.group<NotaryInformationForm>({
      completaryAddress: new FormControl('', { nonNullable: true }),
      address: new FormControl('', { nonNullable: true }),
      country: new FormControl('', { nonNullable: true }),
      email: new FormControl('', {
        validators: [
          ControlValidators.emailFormatValidator(),
          ControlValidators.noSpecialCharactersValidator(),
        ],
        nonNullable: true,
      }),
      name: new FormControl('', { nonNullable: true }),
      firstname: new FormControl('', { nonNullable: true }),
      city: new FormControl('', { nonNullable: true }),
      hasNotary: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      phoneNumber: new FormControl('', { nonNullable: true }),
      zipcode: new FormControl('', { nonNullable: true }),
    });

  public disableCountryInputPlaceholder: string | null = '';

  constructor() {
    this.setNotaryInformationFormDefaultValues();

    this.notaryInformationForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.name?.value?.length || this.firstname?.value?.length) {
          this.notaryInformationForm.addValidators(Validators.required);
          this.notaryInformationForm.setErrors({ fullNameError: true });
          if (this.name?.value?.length && this.firstname?.value?.length) {
            this.notaryInformationForm.clearValidators();
            this.notaryInformationForm.setErrors(null);
          }
        } else {
          this.notaryInformationForm.clearValidators();
          this.notaryInformationForm.setErrors(null);
        }
      });
  }

  public get hasNotary(): FormControl<string> {
    return this.notaryInformationForm.controls.hasNotary;
  }

  public get name(): FormControl<string> {
    return this.notaryInformationForm.controls.name;
  }

  public get country(): FormControl<string> {
    return this.notaryInformationForm.controls.country;
  }

  public get firstname(): FormControl<string> {
    return this.notaryInformationForm.controls.firstname;
  }

  public get email(): FormControl<string> {
    return this.notaryInformationForm.controls.email;
  }

  public get city(): FormControl<string> {
    return this.notaryInformationForm.controls.city;
  }

  public get phoneNumber(): FormControl<string> {
    return this.notaryInformationForm.controls.phoneNumber;
  }

  public get address(): FormControl<string> {
    return this.notaryInformationForm.controls.address;
  }

  public get completaryAddress(): FormControl<string> {
    return this.notaryInformationForm.controls.completaryAddress;
  }

  public get zipcode(): FormControl<string> {
    return this.notaryInformationForm.controls.zipcode;
  }

  public setNotaryInformationFormDefaultValues(): void {
    this.successionFacade.customerInformation$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((customerInformation: CustomerInformation) => {
        this.notaryInformation = customerInformation.notaryInformations;
        this.notaryInformationForm.reset(customerInformation.notaryInformations?.notary);
        if (this.notaryInformation?.hasNotary) {
          this.hasNotary.patchValue(this.notaryInformation?.hasNotary);
        }
        if (customerInformation.notaryInformations?.readOnlyNotary === true) {
          this.notaryInformationForm.disable();
          this.disableCountryInputPlaceholder = this.successionFacade.listBoxItems.countries.filter(
            (country) => country.value === this.country.value,
          )[0].label;
        }
      });
  }

  private navigateToPage(route: RoutesPath): void {
    const notaryInformation: NotaryInformation = {
      hasNotary: this.hasNotary.value || HasNotary.NO,
      notary: this.notaryInformationForm.getRawValue(),
      readOnlyNotary: this.notaryInformation?.readOnlyNotary || false,
    };
    const customerInformation: CustomerInformation = {
      token: this.successionFacade.token,
      savePage: FormStep.FOUR,
      loadPage: route === RoutesPath.PAGE_3 ? FormStep.THREE : FormStep.FIVE,
      notaryInformations: notaryInformation,
      deceasedInformations: undefined,
      deceasedMoreInformations: undefined,
      reporterInformations: undefined,
      inheritanceInformations: undefined,
    };

    this.successionFacade.saveAndLoad(customerInformation);
  }

  public onPrevious(): void {
    this.notaryInformationForm.enable();
    this.navigateToPage(RoutesPath.PAGE_3);
    this.successionFacade.trackPrevious();
  }

  public onNext(): void {
    this.notaryInformationForm.markAllAsTouched();
    if (this.notaryInformationForm.valid || this.notaryInformation?.readOnlyNotary === true) {
      this.navigateToPage(RoutesPath.PAGE_5);

      this.successionFacade.trackNext();
    }
  }
}
